import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// QC sample-login details (F-C1, SPEC-005 SP-C). The Add Sample modal collects extra QC
// metadata (batch/lot, sample type, quantity + unit, sampled-by / received-by members,
// storage condition, a unique sample id). All fields are OPTIONAL — sample creation never
// depends on them.
//
// These are NOT sent on the existing /samples endpoint: the deployed Sample schema rejects
// unknown fields (422). They are persisted SEPARATELY via the real /lims/sampleQc endpoints,
// keyed by the sample id (upsert-by-id; server-audited). See docs/specs/lims-api-contract.md.
//
// state shape:
//   { [sampleId]: { id, batchLot, sampleType, quantity, quantityUnit,
//                   sampledBy, receivedBy, storageCondition, uniqueSampleId } }
const initialState = {}

// Normalise a raw backend record into the slice's per-sample shape.
function toEntry(item) {
  return {
    id: item.id,
    batchLot: item.batchLot || "",
    sampleType: item.sampleType || "",
    quantity: item.quantity != null ? item.quantity : null,
    quantityUnit: item.quantityUnit || "",
    sampledBy: item.sampledBy || null,
    receivedBy: item.receivedBy || null,
    storageCondition: item.storageCondition || "",
    uniqueSampleId: item.uniqueSampleId || "",
  }
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsSampleQc/fetch": {
      const map = {}
      action.res.forEach((item) => {
        map[item.id] = toEntry(item)
      })
      return map
    }
    case "limsSampleQc/set":
      return {
        ...state,
        [action.res.id]: toEntry(action.res),
      }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsSampleQc/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/sampleQc").then((res) => res.items)
})

// Upsert the QC details for one sample. The sample id is the record id, so reads map straight
// back onto the sample. The optional `reason` rides at the request-body TOP LEVEL so the
// server's limsAuditSampleQcSaved audit entry carries it (SPEC-021 m2). `data` carries the QC
// fields; any omitted field is stored as its empty default.
export const setForSample = asyncAction(
  "limsSampleQc/set",
  (_dispatch, sampleId, data, reason = null) => {
    const record = {
      id: sampleId,
      batchLot: (data && data.batchLot) || "",
      sampleType: (data && data.sampleType) || "",
      quantity: data && data.quantity != null ? data.quantity : null,
      quantityUnit: (data && data.quantityUnit) || "",
      sampledBy: (data && data.sampledBy) || null,
      receivedBy: (data && data.receivedBy) || null,
      storageCondition: (data && data.storageCondition) || "",
      uniqueSampleId: (data && data.uniqueSampleId) || "",
    }
    return limsRequest("POST", "/lims/sampleQc", { input: record, reason: reason || undefined }).then(
      () => record
    )
  }
)

// The QC details for one sample (or undefined if none stored yet).
export const selectSampleQc = (state, sampleId) => state.limsSampleQc[sampleId]
